import { type FormEvent, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BugIcon, KeyRoundIcon } from "lucide-react";

import { useAuth } from "../app/AuthContext";
import { useLogin } from "../app/queries";
import { Button } from "../components/ui/button";
import { Field, FieldDescription, FieldGroup, FieldLabel } from "../components/ui/field";
import { Input } from "../components/ui/input";
import { Alert, AlertDescription } from "../components/ui/alert";

export function LoginPage() {
  const navigate = useNavigate();
  const { setToken } = useAuth();
  const [tokenInput, setTokenInput] = useState("");
  const [error, setError] = useState<string | null>(null);
  const login = useLogin();

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const trimmed = tokenInput.trim();
    if (!trimmed) {
      setError("Enter your MU_ADMIN_TOKEN to continue.");
      return;
    }
    setError(null);
    try {
      await login.mutateAsync(trimmed);
      setToken(trimmed);
      navigate("/", { replace: true });
    } catch (err: unknown) {
      setToken("");
      const message = err instanceof Error ? err.message : "Login failed";
      setError(message);
    }
  }

  return (
    <div className="flex h-svh w-full items-center justify-center bg-background">
      <div className="flex w-full max-w-sm flex-col items-center gap-6 px-4 py-10 sm:px-6 sm:py-12">
        <div className="flex flex-col items-center gap-3 text-center">
          <span className="grid size-12 place-items-center rounded-2xl bg-foreground text-background shadow-sm">
            <BugIcon className="size-6" />
          </span>
          <div>
            <h1 className="text-lg font-semibold tracking-tight">
              Sign in to Mushi
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Enter your administrator token to continue.
            </p>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="login-form"
          className="flex w-full flex-col gap-4 rounded-xl border bg-card p-5 shadow-sm"
        >
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="login-token">Admin token</FieldLabel>
              <div className="relative">
                <KeyRoundIcon className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="login-token"
                  name="token"
                  type="password"
                  autoComplete="off"
                  required
                  className="pl-8 font-mono"
                  value={tokenInput}
                  onChange={(e) => setTokenInput(e.target.value)}
                  placeholder="paste token"
                />
              </div>
              <FieldDescription>
                Set <code className="font-mono">MU_ADMIN_TOKEN</code> on the
                server to enable administrator access.
              </FieldDescription>
            </Field>
            {error ? (
              <Alert variant="destructive" data-testid="login-error">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : null}
            <Field orientation="horizontal" className="items-center">
              <Button render={<Link to="/" />} nativeButton={false} type="button" variant="ghost" size="sm">
                Cancel
              </Button>
              <Button
                type="submit"
                size="sm"
                className="ml-auto"
                disabled={login.isPending}
                data-testid="login-submit"
              >
                {login.isPending ? "Checking…" : "Continue"}
              </Button>
            </Field>
          </FieldGroup>
        </form>

        <p className="text-xs text-muted-foreground">
          Need help? See the project README for environment setup.
        </p>
      </div>
    </div>
  );
}
